import { startCase } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Vector3Tuple } from 'three';
import { Euler, Quaternion, Vector3 } from 'three';
import { degToRad } from 'three/src/math/MathUtils.js';

import {
  Close,
  ContentCopy,
  Done,
  VFlex,
  VIconButton,
  VInput,
  VStack,
  new__cn as cn,
  useFlag,
} from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useBoxClipboardStore,
  useEditingLabelBoxStore,
  useToolMenuStore,
  useToolPermissionStore,
} from '@/store';
import type { Box } from '@/type';
import { getVectorByAxis, toDegree } from '@/util';

type BoxPropNames = keyof Pick<Box, 'position' | 'dimension' | 'rotation'> &
  string;

const ROTATION_STEP = 10;

const BOX_PROPS = ['position', 'dimension', 'rotation'] as BoxPropNames[];
const BOX_PROP_ATTR_MAP: {
  [K in BoxPropNames]: string[];
} = {
  position: ['x', 'y', 'z'],
  dimension: ['l', 'w', 'h'],
  rotation: ['x', 'y', 'z'],
};
const DEFAULT_BOX_PROP_ATTR_INPUT_PROPS_MAP: {
  [K in BoxPropNames]: {
    max?: number;
    min?: number;
    step?: number;
    readOnly?: boolean;
  };
} = {
  position: {
    step: 0.2,
  },
  dimension: {
    min: 0.5,
    step: 0.1,
  },
  rotation: {
    step: ROTATION_STEP,
  },
};

const TrackIDPasteNotification = ({
  copiedTrackID,
  onPaste,
}: {
  copiedTrackID: string;
  onPaste: () => void;
}) => {
  const { t } = useTranslation();
  const { value: visible, setFalse: hide } = useFlag(true);

  return (
    <VFlex
      className={cn(
        'w-full rounded-sm bg-skyBlue-300 px-scale/8 py-scale/4 shadow-sm',
        !visible && 'hidden',
      )}
      gap={0.25}
      items="center"
      justify="between"
    >
      <div className="text-body-sm">
        {t('labeling.boxInspectorPasteTrackID.duplication')}
      </div>
      <VFlex gap={0.25} items="center" justify="between">
        <div className="text-body-md tracking-tight">{copiedTrackID}</div>
        <Done
          className="size-4 text-primary-500 hover:text-primary-400 active:text-primary-600"
          role="button"
          onClick={() => onPaste()}
        />
        <Close
          className="size-3 text-gray-400 hover:text-gray-300 active:text-gray-500"
          role="button"
          onClick={() => hide()}
        />
      </VFlex>
    </VFlex>
  );
};

const ToolBoxPropsController = ({ close }: { close: () => void }) => {
  const { t } = useTranslation();
  const {
    selectedLabelingBoxList: selectedBoxList,
    moveBoxList,
    resizeBoxList,
    updateQuaternion,
  } = useLabelingBoxContext();
  const permission = useToolPermissionStore(state => state.permission);
  const transformedBoxData = useEditingLabelBoxStore(
    state => state.transformedBoxData,
  );
  const copiedTrackID = useBoxClipboardStore(state => state.copiedTrackID);
  const isLabelUpdatable = permission.labeling.updatable;
  const selectedBox = selectedBoxList?.[0];
  const props = useMemo(() => {
    const boxData = useEditingLabelBoxStore.getState().isTransforming
      ? {
          position: transformedBoxData.position,
          dimension: transformedBoxData.dimension,
          rotation: new Euler()
            .setFromQuaternion(new Quaternion(...transformedBoxData.quaternion))
            .toArray()
            .slice(0, 3) as [number, number, number],
        }
      : {
          position: selectedBox?.position ?? [0, 0, 0],
          dimension: selectedBox?.dimension ?? [0, 0, 0],
          rotation: selectedBox?.getRotation() ?? [0, 0, 0],
        };

    const position = boxData.position.map(v => +v.toFixed(2));
    const dimension = boxData.dimension.map(v => +v.toFixed(2));
    const rotation = boxData.rotation.map(v =>
      typeof v === 'number' && !isNaN(v) ? +toDegree(v).toFixed(2) : v,
    );

    return {
      position,
      dimension,
      rotation,
    };
  }, [selectedBox, transformedBoxData]);

  const changeBoxTransform = useCallback(
    <P extends BoxPropNames>(prop: P, index: number, value: number) => {
      const id = selectedBox?.id;

      if (!id) return;

      if (prop === 'rotation') {
        const offset = getVectorByAxis(index as 0 | 1 | 2);
        const quaternion = selectedBox.boxMesh.quaternion;

        offset.applyQuaternion(quaternion);

        const boxPos = selectedBox.boxMesh.position;
        const abstractCameraPos = new Vector3(...boxPos).add(offset);
        const axis = boxPos.sub(abstractCameraPos).normalize();

        const dir =
          value - (props.rotation as Vector3Tuple)[index] > 0 ? 1 : -1;

        const rotationQuat = new Quaternion().setFromAxisAngle(
          axis,
          -degToRad(ROTATION_STEP) * dir,
        );
        const newQuat = rotationQuat
          .multiply(new Quaternion(...quaternion))
          .toArray();

        updateQuaternion([[id, newQuat]]);

        return;
      }

      const propValue = props[prop].map((v, i) =>
        i === index ? value : v,
      ) as Vector3Tuple;

      switch (prop) {
        case 'position':
          useEditingLabelBoxStore.getState().updateTransformedBoxData({
            position: propValue,
          });
          moveBoxList([[id, propValue]]);
          break;
        case 'dimension':
          resizeBoxList([[id, propValue]]);
          break;
      }
    },
    [props],
  );

  return (
    <VStack
      className="w-[308px] overflow-hidden rounded-md bg-white p-2"
      gap={0.5}
    >
      <VFlex items="center" justify="between">
        <div className="text-body-lg font-bold text-blue-300">
          {t('labeling.boxInspectorTitle.nonprojection')}
        </div>
        <Close className="size-4 text-gray-400" role="button" onClick={close} />
      </VFlex>

      <VStack>
        <div className="text-body-md font-semibold">
          {t('labeling.boxInspectorIdentificationTitle.cunette')}
        </div>
        <VStack className="w-full">
          <VInput
            fluid
            label="Track ID"
            inputClassName="text-right"
            orientation="horizontal"
            readOnly
            size="sm"
            type="text"
            value={selectedBox?.trackId}
            renderRightSide={() =>
              selectedBox?.trackId ? (
                <VIconButton
                  Icon={ContentCopy}
                  size="xs"
                  variant="roundPrimary"
                  onClick={() => {
                    useToolMenuStore.getState().selectByName('copyTrackID');
                  }}
                />
              ) : null
            }
          />
        </VStack>
        {permission.labeling.updatable &&
          copiedTrackID &&
          selectedBox?.trackId !== copiedTrackID && (
            <TrackIDPasteNotification
              copiedTrackID={copiedTrackID}
              onPaste={() => {
                useToolMenuStore.getState().selectByName('pasteTrackID');
              }}
            />
          )}
      </VStack>

      <VStack className="w-full" gap={0.5}>
        {BOX_PROPS.map(prop => {
          return (
            <VFlex direction="col" className="w-full gap-[2px]" key={prop}>
              <div className="text-body-md font-semibold">
                {startCase(prop)}
              </div>
              <VFlex justify="between" gap={0.5}>
                {BOX_PROP_ATTR_MAP[prop].map((childProp, i) => {
                  return (
                    <VInput
                      label={childProp}
                      size="sm"
                      type="number"
                      orientation="horizontal"
                      value={props[prop][i]}
                      readOnly={!selectedBox || !isLabelUpdatable}
                      onChange={e =>
                        changeBoxTransform(prop, i, +e.target.value)
                      }
                      {...DEFAULT_BOX_PROP_ATTR_INPUT_PROPS_MAP[prop]}
                      key={i}
                    />
                  );
                })}
              </VFlex>
            </VFlex>
          );
        })}
      </VStack>
    </VStack>
  );
};

ToolBoxPropsController.displayName = 'ToolBoxPropsController';

export default ToolBoxPropsController;
